import initApi from '@/api/initApi';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import type {
  V1ApiCreateVendorV1OrganizationsOrganizationIdVendorsPostRequest,
  V1ApiDeleteDocumentV1OrganizationsOrganizationIdVendorsVendorIdDocumentsDocumentIdDeleteRequest,
  V1ApiDeleteVendorV1OrganizationsOrganizationIdVendorsVendorIdDeleteRequest,
  V1ApiDownloadVendorDocumentV1OrganizationsOrganizationIdVendorsVendorIdDocumentsDocumentIdDownloadGetRequest,
  V1ApiGetVendorV1OrganizationsOrganizationIdVendorsVendorIdGetRequest,
  V1ApiListDocumentsForVendorV1OrganizationsOrganizationIdVendorsVendorIdDocumentsGetRequest,
  V1ApiListVendorsV1OrganizationsOrganizationIdVendorsGetRequest,
  V1ApiUpdateVendorV1OrganizationsOrganizationIdVendorsVendorIdPutRequest,
  V1ApiUploadDocumentForVendorV1OrganizationsOrganizationIdVendorsVendorIdDocumentsPostRequest,
} from '@/clients/v1';
import { V1Api } from '@/clients/v1';

import { handleApiError } from '@/api/handleApiError.ts';
import type { RequestConfig } from '@/api/Request';
import type { AxiosError } from 'axios';
import type { PageDocumentOverview, PageVendorDetails, VendorDetails } from '@/clients/v1';

export const useVendorStore = defineStore('vendor', () => {
  const vendorsData = ref<PageVendorDetails | null>(null);
  const currentVendor = ref<VendorDetails | null>(null);
  const vendorDocumentsData = ref<PageDocumentOverview | null>(null);

  const getVendors = async (
    params: V1ApiListVendorsV1OrganizationsOrganizationIdVendorsGetRequest,
    options?: RequestConfig,
  ) => {
    try {
      const v1Api = await initApi(V1Api);

      const response = await v1Api?.listVendorsV1OrganizationsOrganizationIdVendorsGet(
        params,
        options,
      );

      if (!response) return;
      vendorsData.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getVendor = async (
    params: V1ApiGetVendorV1OrganizationsOrganizationIdVendorsVendorIdGetRequest,
  ) => {
    try {
      const v1Api = await initApi(V1Api);

      const response =
        await v1Api?.getVendorV1OrganizationsOrganizationIdVendorsVendorIdGet(params);

      if (!response) return;
      currentVendor.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const createVendor = async (
    params: V1ApiCreateVendorV1OrganizationsOrganizationIdVendorsPostRequest,
  ) => {
    try {
      const v1Api = await initApi(V1Api);

      const response = await v1Api?.createVendorV1OrganizationsOrganizationIdVendorsPost(params);
      if (!response) return;
      currentVendor.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateVendor = async (
    params: V1ApiUpdateVendorV1OrganizationsOrganizationIdVendorsVendorIdPutRequest,
  ) => {
    try {
      const v1Api = await initApi(V1Api);

      const response =
        await v1Api?.updateVendorV1OrganizationsOrganizationIdVendorsVendorIdPut(params);

      if (!response) return;
      currentVendor.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const deleteVendor = async (
    params: V1ApiDeleteVendorV1OrganizationsOrganizationIdVendorsVendorIdDeleteRequest,
  ) => {
    try {
      const v1Api = await initApi(V1Api);

      await v1Api?.deleteVendorV1OrganizationsOrganizationIdVendorsVendorIdDelete(params);
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getVendorDocumentsList = async (
    params: V1ApiListDocumentsForVendorV1OrganizationsOrganizationIdVendorsVendorIdDocumentsGetRequest,
    options?: RequestConfig,
  ) => {
    try {
      const v1Api = await initApi(V1Api);

      const response =
        await v1Api?.listDocumentsForVendorV1OrganizationsOrganizationIdVendorsVendorIdDocumentsGet(
          params,
          options,
        );

      if (!response) return;

      vendorDocumentsData.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const uploadVendorDocument = async (
    params: V1ApiUploadDocumentForVendorV1OrganizationsOrganizationIdVendorsVendorIdDocumentsPostRequest,
  ) => {
    try {
      const v1Api = await initApi(V1Api);

      const response =
        await v1Api?.uploadDocumentForVendorV1OrganizationsOrganizationIdVendorsVendorIdDocumentsPost(
          params,
        );

      if (!response) return null;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const downloadVendorDocument = async (
    params: V1ApiDownloadVendorDocumentV1OrganizationsOrganizationIdVendorsVendorIdDocumentsDocumentIdDownloadGetRequest,
    fileName: string,
  ) => {
    try {
      const v1Api = await initApi(V1Api);
      const response =
        await v1Api?.downloadVendorDocumentV1OrganizationsOrganizationIdVendorsVendorIdDocumentsDocumentIdDownloadGet(
          params,
          {
            responseType: 'blob',
          },
        );

      if (!response) {
        return;
      }

      const blob = response.data as unknown as Blob;
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const deleteVendorDocument = async (
    params: V1ApiDeleteDocumentV1OrganizationsOrganizationIdVendorsVendorIdDocumentsDocumentIdDeleteRequest,
  ) => {
    try {
      const v1Api = await initApi(V1Api);

      await v1Api?.deleteDocumentV1OrganizationsOrganizationIdVendorsVendorIdDocumentsDocumentIdDelete(
        params,
      );
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const resetStore = () => {
    vendorsData.value = null;
    currentVendor.value = null;
    vendorDocumentsData.value = null;
  };

  return {
    getVendors,
    vendorsData,
    getVendor,
    currentVendor,
    createVendor,
    updateVendor,
    deleteVendor,
    uploadVendorDocument,
    downloadVendorDocument,
    getVendorDocumentsList,
    vendorDocumentsData,
    deleteVendorDocument,
    resetStore,
  };
});
